import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal from '../../../components/Modal/Modal';
import { useBulkOperations } from '../hooks/useImports';
import { useReceiptUpload } from '../hooks/useReceiptUpload';

import CreateImportModalFooter from './CreateImportModalFooter';
import FileSelectionPanel from './FileSelectionPanel';
import ImportFormFields from './ImportFormFields';

interface CreateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateImportModal: React.FC<CreateImportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { createBulkOperationAsync, isCreating } = useBulkOperations();
  const {
    trackedFiles,
    addFiles,
    removeFile,
    reset,
    allDone,
    isProcessing,
    receiptIds,
    hasErrors,
  } = useReceiptUpload();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleFilesAdded = (selectedFiles: FileList) => {
    const fileArray = Array.from(selectedFiles);
    const addedCount = addFiles(fileArray);

    if (addedCount < fileArray.length) {
      setError('Only PDF files are allowed');
      setTimeout(() => setError(''), 3000);
    }
  };

  const canCreate =
    title.trim().length > 0 &&
    trackedFiles.length > 0 &&
    allDone &&
    !hasErrors &&
    !isCreating;

  const handleCreate = useCallback(async () => {
    if (!canCreate) return;

    try {
      setError('');

      const createdBulkOperation = await createBulkOperationAsync({
        receiptIds,
        title: title.trim(),
        description: description.trim() || undefined,
      });

      // Reset form and close
      setTitle('');
      setDescription('');
      reset();
      setError('');
      onClose();

      navigate(`/bulk-upload/${createdBulkOperation.id}`);
    } catch (err) {
      console.error('Failed to create bulk operation:', err);
      setError(err instanceof Error ? err.message : 'Failed to create import');
    }
  }, [
    canCreate,
    receiptIds,
    title,
    description,
    createBulkOperationAsync,
    reset,
    onClose,
    navigate,
  ]);

  const handleCancel = useCallback(() => {
    setTitle('');
    setDescription('');
    reset();
    setError('');
    onClose();
  }, [onClose, reset]);

  return (
    <Modal
      isOpen={isOpen}
      title='New Bulk Upload'
      onClose={handleCancel}
      size='lg'
      footer={
        <CreateImportModalFooter
          onCancel={handleCancel}
          onCreate={handleCreate}
          isCreating={isCreating}
          disabled={!canCreate}
        />
      }
    >
      <div className='space-y-6'>
        <ImportFormFields
          title={title}
          description={description}
          onTitleChange={setTitle}
          onDescriptionChange={setDescription}
        />

        <FileSelectionPanel
          trackedFiles={trackedFiles}
          onFilesAdded={handleFilesAdded}
          onRemoveFile={removeFile}
          disabled={isCreating}
        />

        {/* Processing indicator */}
        {isProcessing && (
          <p className='text-sm text-blue-600 dark:text-blue-400 italic'>
            Uploading and processing files...
          </p>
        )}

        {/* Error Message */}
        {error && (
          <div className='p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
            <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateImportModal;
